import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, RefreshCcw, Heart, MessageCircle, Send, CheckCircle2, HelpCircle } from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const SocialWorkshop = () => {
  const [gameState, setGameState] = useState("start");
  const [reel, setReel] = useState({ hook: "", transition: "", cta: "" });
  const [engagement, setEngagement] = useState(0);
  const [score, setScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);

  const hooks = ["Shocking Fact", "Visual Hook", "Question", "Problem/Solution"];
  const transitions = ["Jump Cut", "Match Cut", "Zoom In", "Wipe"];
  const ctas = ["Follow for more", "Check Link", "Save this", "Share"];

  const calculateEngagement = useCallback(() => {
    if (!reel.hook || !reel.transition || !reel.cta) return 0;
    // Simple logic for engagement
    let eng = 20;
    if (reel.hook === "Visual Hook") eng += 30;
    if (reel.transition === "Match Cut") eng += 20;
    if (reel.cta === "Save this") eng += 30;
    return eng;
  }, [reel]);

  useEffect(() => {
    const eng = calculateEngagement();
    setEngagement(eng);
    if (gameState === "playing" && reel.hook && reel.transition && reel.cta) {
      if (eng >= 80) {
        setGameState("success");
        setScore(s => s + 1);
      }
    }
  }, [reel, gameState, calculateEngagement]);

  return (
    <section id="workshop" className="py-24 relative overflow-hidden bg-[#0a0a0a]">
      <div className="container-xl px-6">
        <div className="flex flex-col mb-12">
          <div className="text-left mb-8">
            <Kicker index="SOC-01" label="Engagement Lab" />
          </div>
          <div className="text-center">
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--cream)]">
              Viral <span className="italic gold-text">Architect</span>
            </h2>
            <p className="mt-4 text-[var(--cream-muted)] text-sm uppercase tracking-[0.2em] max-w-lg mx-auto">
              Craft the perfect reel sequence to maximize audience engagement.
            </p>
            <button
              onClick={() => setShowInstructions(true)}
              className="mt-6 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors mx-auto text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Play
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center bg-[#111] p-8 md:p-16 rounded-[40px] border border-[var(--rule)] relative shadow-2xl">

          {/* Reel Preview */}
          <div className="relative aspect-[9/16] max-h-[600px] bg-black rounded-3xl overflow-hidden border border-[var(--rule)] mx-auto w-full max-w-[300px] flex flex-col shadow-[0_0_30px_rgba(0,0,0,0.7)]">
            
            {/* Background Live Instagram Embed Video */}
            <div className="absolute inset-0 z-0">
              <iframe
                src="https://www.instagram.com/reel/DYZqx2OS75U/embed/"
                className="w-full h-full border-0 pointer-events-none"
                scrolling="no"
                allowFullScreen
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                style={{
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                  background: "#0d0c0a"
                }}
              />
              {/* Semi-transparent dark overlay to keep text highly readable */}
              <div className="absolute inset-0 bg-black/60 backdrop-blur-[0.5px]" />
            </div>

            <div className="flex-1 flex items-center justify-center p-6 text-center relative z-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={reel.hook + reel.transition + reel.cta}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-4"
                >
                  {reel.hook && <div className="text-[var(--gold)] font-display text-2xl uppercase italic drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">{reel.hook}</div>}
                  {reel.transition && <div className="text-white font-body text-xs opacity-90 bg-black/50 px-3 py-1 rounded-full border border-white/5 backdrop-blur-xs inline-block">[{reel.transition}]</div>}
                  {reel.cta && <div className="text-white font-impact text-lg mt-8 bg-[var(--gold)] text-black px-4 py-2 rounded-full shadow-lg inline-block">👉 {reel.cta}</div>}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Controls */}
          <div className="relative z-40">
            {gameState === "start" ? (
              <div className="text-center py-10 flex flex-col items-center">
                <Share2 className="w-16 h-16 text-[var(--gold)] mb-8 opacity-50" />
                <h3 className="font-display text-3xl text-white mb-8 italic">Ready to go viral?</h3>
                <button className="btn-primary px-12" onClick={() => setGameState("playing")}>Start Build</button>
              </div>
            ) : (
              <div className="space-y-10">
                <div className="space-y-4">
                  <label className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold)] font-impact">The Hook</label>
                  <div className="flex flex-wrap gap-2">
                    {hooks.map(h => (
                      <button
                        key={h}
                        onClick={() => setReel({ ...reel, hook: h })}
                        className={`px-4 py-2 rounded-full text-xs transition-all border ${reel.hook === h ? "bg-[var(--gold)] text-black border-[var(--gold)]" : "bg-white/5 text-white border-white/10 hover:border-white/30"}`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold)] font-impact">Transition</label>
                  <div className="flex flex-wrap gap-2">
                    {transitions.map(t => (
                      <button
                        key={t}
                        onClick={() => setReel({ ...reel, transition: t })}
                        className={`px-4 py-2 rounded-full text-xs transition-all border ${reel.transition === t ? "bg-[var(--gold)] text-black border-[var(--gold)]" : "bg-white/5 text-white border-white/10 hover:border-white/30"}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] uppercase tracking-[0.4em] text-[var(--gold)] font-impact">Call to Action</label>
                  <div className="flex flex-wrap gap-2">
                    {ctas.map(c => (
                      <button
                        key={c}
                        onClick={() => setReel({ ...reel, cta: c })}
                        className={`px-4 py-2 rounded-full text-xs transition-all border ${reel.cta === c ? "bg-[var(--gold)] text-black border-[var(--gold)]" : "bg-white/5 text-white border-white/10 hover:border-white/30"}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  className="w-full py-4 border border-[var(--rule)] rounded-full text-[var(--cream-muted)] hover:text-[var(--gold)] hover:border-[var(--gold)] transition-all uppercase tracking-[0.3em] text-[10px] flex items-center justify-center gap-3"
                  onClick={() => setReel({ hook: "", transition: "", cta: "" })}
                >
                  <RefreshCcw size={14} /> Reset Reel
                </button>
              </div>
            )}
          </div>

          <AnimatePresence>
            {gameState === "success" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/95 backdrop-blur-xl z-50 rounded-[40px] flex flex-col items-center justify-center p-12 text-center"
              >
                <CheckCircle2 className="text-[var(--gold)] w-20 h-20 mb-8" />
                <h3 className="font-display text-5xl text-white mb-4">REEL <span className="italic gold-text">VIRAL</span></h3>
                <p className="text-[var(--cream-muted)] mb-10">Your content strategy is hitting the algorithms perfectly.</p>
                <button className="btn-primary px-12" onClick={() => { setReel({ hook: "", transition: "", cta: "" }); setGameState("playing"); }}>Build Next Reel</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <GameInstructionsModal
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="Viral Architect Lab"
        instructions={[
          { title: "Select a Hook", desc: "Choose the opening visual or verbal 'Hook' from the sidebar. This determines how many users stop scrolling to watch." },
          { title: "Choose a Transition", desc: "Pick a cinematic transition style to maintain flow and keep the audience's attention during the mid-reel phase." },
          { title: "Apply a CTA", desc: "Select a strong 'Call to Action' to convert viewers into followers or customers at the end of the sequence." },
          { title: "Go Viral", desc: "Combine the right elements to reach over 80% engagement and go viral. Each successful sequence adds to your 'Viral Score'." }
        ]}
      />
    </section>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default SocialWorkshop;
